import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { Condition, MultiselectField, NumberField, SelectField } from '../../FinalForm';
import React from 'react';

export function PreviousRegistration({ domainsForPreviousRegReq, affiliatesForPreviousRegReq, source, isLoading }) {
  const generateName = (field) => (source ? `${source}.${field}` : field);
  return (
    <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
      <Grid size={{ xs: 12, lg: 2 }}>
        <Typography variant='h5'>Previous Registrations Required</Typography>
      </Grid>
      <Grid size={{ xs: 12, lg: 4 }}>
        <SelectField
          name={generateName('previousRegistrationConfiguration.andOrLogic')}
          options={[
            {
              value: null,
              option: 'Select',
            },
            {
              value: 'AND',
              option: 'All entries must pass',
            },
            {
              value: 'OR',
              option: 'Any of these entries can pass',
            },
          ]}
        />
      </Grid>

      <Grid size={{ xs: 12, lg: 8 }}>
        <FieldArray name={generateName('previousRegistrationConfiguration.previousRegistrations')}>
          {({ fields }) => {
            return fields.map((fieldName, index) => {
              return (
                <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'start' }}>
                  <Condition
                    key={index}
                    when={generateName('previousRegistrationConfiguration.andOrLogic')}
                    is={['AND', 'OR']}
                  >
                    {fields.length > 1 && (
                      <Grid size={{ xs: 12, lg: 1 }}>
                        <Tooltip title='Remove Previous Registration'>
                          <IconButton
                            color='error'
                            type='button'
                            aria-label='Remove Previous Registration'
                            onClick={() => fields.remove(index)}
                          >
                            <Close fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, lg: 2 }}>
                      <SelectField
                        name={`${fieldName}.includeExcludeAction`}
                        options={[
                          {
                            value: null,
                            option: 'None',
                          },
                          {
                            value: 'Include',
                            option: 'Include',
                          },
                          {
                            value: 'Exclude',
                            option: 'Exclude',
                          },
                        ]}
                        parse={(value) =>
                          value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value
                        }
                        format={(value) =>
                          value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Field name={`${fieldName}.domains`}>
                        {({ input, meta }) => (
                          <>
                            <MultiselectField
                              {...input}
                              data={domainsForPreviousRegReq}
                              textField={(item) => {
                                // Handle both strings and objects
                                if (typeof item === 'string') {
                                  return item;
                                }
                                if (item && typeof item === 'object') {
                                  return item.name || 'Unknown Item';
                                }
                                return 'Invalid Item';
                              }}
                              placeholder={'Select Domains'}
                              busy={isLoading}
                              groupBy={(item) => {
                                if (typeof item === 'string') {
                                  return item.startsWith('#') ? 0 : 1;
                                }
                                if (item && typeof item === 'object' && item.name) {
                                  return item.name.startsWith('#') ? 0 : 1;
                                }
                                return 1;
                              }}
                              renderListGroup={({ group }) => (group === 0 ? <span>Tags</span> : <span>Domains</span>)}
                            />
                          </>
                        )}
                      </Field>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Field name={`${fieldName}.affiliates`}>
                        {({ input, meta }) => (
                          <>
                            <MultiselectField
                              {...input}
                              data={affiliatesForPreviousRegReq}
                              textField={(item) => {
                                // Handle both strings and objects
                                if (typeof item === 'string') {
                                  return item;
                                }
                                if (item && typeof item === 'object') {
                                  return item.name || 'Unknown Item';
                                }
                                return 'Invalid Item';
                              }}
                              placeholder={'Select Affiliates'}
                              busy={isLoading}
                              groupBy={(item) => {
                                if (typeof item === 'string') {
                                  return item.startsWith('#') ? 0 : 1;
                                }
                                if (item && typeof item === 'object' && item.name) {
                                  return item.name.startsWith('#') ? 0 : 1;
                                }
                                return 1;
                              }}
                              renderListGroup={({ group }) =>
                                group === 0 ? <span>Tags</span> : <span>Affiliates</span>
                              }
                            />
                          </>
                        )}
                      </Field>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 2 }}>
                      <NumberField name={`${fieldName}.previousRegistrationsMin`} label='Min' noLabel />
                    </Grid>
                    <Grid size={{ xs: 12, lg: 2 }}>
                      <NumberField name={`${fieldName}.previousRegistrationsMax`} label='Max' noLabel />
                    </Grid>
                    {index === fields.length - 1 && (
                      <Grid size={{ xs: 12, lg: 1 }} offset={{ lg: 11 }}>
                        <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
                          <Tooltip title='Add Previous Registration'>
                            <IconButton
                              color='success'
                              aria-label='Add Previous Registration'
                              onClick={() =>
                                fields.push({
                                  domains: [],
                                  affiliates: [],
                                  includeExcludeAction: null,
                                  previousRegistrationsMin: null,
                                  previousRegistrationsMax: null,
                                })
                              }
                            >
                              <Add />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    )}
                  </Condition>
                </Grid>
              );
            });
          }}
        </FieldArray>
      </Grid>
    </Grid>
  );
}
